import {
  isPartialReferencedImplantCharacteristics,
  type ImplantCharacteristics,
  type ImplantReference,
} from "@jasper/client-models";
import { Input, Stack, Switch, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { FieldValueProps } from "../../types";
import { useState } from "react";
import { UnreferencedImplantCharacteristicsForm } from "./UnreferencedImplantCharacteristicsForm";
import { ReferencedImplantCharacteristicsForm } from "./ReferencedImplantCharacteristicsForm";
import { handleDivBlur } from "../../utils";

interface Props extends FieldValueProps<ImplantCharacteristics> {
  implantReferences: ImplantReference[];
  implantReferencesAreLoading?: boolean;
}

export const ImplantCharacteristicsForm = ({
  value,
  errors,
  onChange,
  implantReferences,
  implantReferencesAreLoading,
  onBlur,
}: Props) => {
  const { t } = useTranslation("forms", {
    keyPrefix: "forms.implantCharacteristics",
  });

  const [isReferenceMissing, setIsReferenceMissing] = useState<boolean>(
    !!value && "implantBrand" in value
  );

  const useUnreferenceImplant =
    isReferenceMissing &&
    !isPartialReferencedImplantCharacteristics(value ?? {});

  const handleSwitchChange = (checked: boolean) => {
    setIsReferenceMissing(checked);
    if (checked) {
      onChange({
        scanBodyReference: value?.scanBodyReference,
        implantReferenceId: undefined,
      });
    } else {
      onChange({
        scanBodyReference: value?.scanBodyReference,
        implantBrand: undefined,
        implantSystem: undefined,
      });
    }
  };

  return (
    <Stack gap="md" onBlur={event => handleDivBlur(event, onBlur)}>
      <Stack gap="sm">
        {useUnreferenceImplant ? (
          <UnreferencedImplantCharacteristicsForm
            value={value}
            errors={errors}
            onChange={onChange}
          />
        ) : (
          <ReferencedImplantCharacteristicsForm
            value={value}
            errors={errors}
            onChange={onChange}
            implantReferences={implantReferences}
            implantReferencesAreLoading={implantReferencesAreLoading}
          />
        )}
        <Switch
          checked={useUnreferenceImplant}
          label={t("labels.missingReference")}
          onClick={() => handleSwitchChange(!useUnreferenceImplant)}
        />
      </Stack>
      <Stack>
        <Text variant="strong">{t("subtitles.scanBody")}</Text>
        <Input.Wrapper
          label={t("labels.scanBody")}
          description={t("descriptions.scanBody")}
          error={errors?.scanBodyReference ? t("errors.scanBody") : undefined}
        >
          <Input
            placeholder={t("placeholders.scanBody")}
            value={value?.scanBodyReference ?? ""}
            error={errors?.scanBodyReference ? t("errors.scanBody") : undefined}
            onChange={e =>
              onChange({ ...value, scanBodyReference: e.target.value })
            }
          />
        </Input.Wrapper>
      </Stack>
    </Stack>
  );
};
